'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { format } from 'date-fns';
import { de, enUS, es, fr, it, pt } from 'date-fns/locale';
import type { Locale as DateFnsLocale } from 'date-fns';
import { Clock, Trophy, DollarSign, Wallet, FileText, Plus, Building2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PokerSiteLogo } from '../Dashboard/PokerSiteLogo';
import { cn } from '@/lib/utils';
import { minutesToHoursMinutes, hoursMinutesToMinutes, durationInputValue } from '@/lib/duration';
import { useStepperContext } from '../ui/stepper';
import { useSessionFooter } from './NewSessionStepper';
import { SUPPORTED_CURRENCIES } from '@/lib/currencies';

const CURRENCIES = SUPPORTED_CURRENCIES.map((currency) => ({ code: currency.code, label: currency.code }));
const LIMITS = ['1/2', '1/3', '2/4', '2/5', '5/5', '5/10', '10/20', '25/50', '50/100'];
const VARIANTS = [
  "No Limit Hold'em",
  "Limit Hold'em",
  'OMAHA 4',
  'OMAHA 5',
  'OMAHA HI/LO',
  'SHORT TRACK',
  '8-GAME',
  'HORSE',
  'SHORT DECK',
  'RAZZ',
  'DEALER CHOICE',
];

interface Bankroll {
  id: string;
  siteName: string;
  currentBalance: string;
  currency: string;
  type: string;
}

interface UserFestivalOption {
  id: string;
  name: string;
  currency: string | null;
  pokerFestivalId: string | null;
  startDate: string | Date | null;
  endDate: string | Date | null;
  venue: string | null;
  countryCode: string | null;
  userTournaments: Array<{
    id: string;
    name: string;
    pokerTournamentId: string | null;
    buyInAmount: number | null;
  }>;
}

interface PokerFestivalOption {
  id: string;
  name: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  venue: string | null;
  countryCode: string | null;
}

interface TournamentOption {
  value: string;
  label: string;
  userTournamentId: string | null;
  pokerTournamentId: string | null;
  buyInAmount: number | null;
}

/** Recherche insensible à la casse et aux accents (WSOP ≈ wsop). */
function normalizeFestivalSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
}

const DATE_FNS_BY_APP_LOCALE: Record<string, DateFnsLocale> = {
  fr,
  en: enUS,
  es,
  de,
  it,
  pt,
};

function parseIsoDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function festivalSubtitleParts(
  startDate: string | Date | null,
  endDate: string | Date | null,
  venue: string | null,
  dateLocale: DateFnsLocale
): { datePart: string; venuePart: string } {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  const datePart =
    start && end
      ? `${format(start, 'd MMM', { locale: dateLocale })} - ${format(end, 'd MMM yyyy', { locale: dateLocale })}`
      : '';
  const venuePart = venue?.trim() ?? '';
  return { datePart, venuePart };
}

function FestivalFlag({ countryCode }: { countryCode: string | null }) {
  const flagUrl =
    countryCode && /^[A-Za-z]{2}$/.test(countryCode)
      ? `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`
      : null;
  return (
    <span className="flex-shrink-0 w-6 h-[18px] flex items-center justify-center overflow-hidden rounded-sm mt-0.5 bg-gray-100 dark:bg-gray-700">
      {flagUrl ? (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img src={flagUrl} alt="" width={24} height={18} className="w-6 h-[18px] object-cover rounded-sm" loading="lazy" />
      ) : (
        <span className="w-5 h-[14px] block rounded-sm bg-gray-200 dark:bg-gray-600" aria-hidden />
      )}
    </span>
  );
}

export type SpinGoDetailEntry = {
  /** Label d'affichage (ex: "Ligne 1", "Normal", "Nitro") */
  gameType: string;
  durationMinutes?: string;
  buyInLevel?: string;
  tournamentCount: string;
  buyInTotal: string;
  rackback: string;
  /** Gains ou pertes pour cette ligne */
  profit?: string;
};

interface SelectedBankroll {
  bankrollId: string;
  newBalance: string;
  rackback?: string;
  /** MTT Online: nombre de tournois pour cette bankroll */
  tournamentCount?: string;
  /** MTT Online: buy-in total pour cette bankroll */
  buyInTotal?: string;
  /** Spin & Go : détail Normal / Nitro (optionnel) */
  spinGoDetails?: SpinGoDetailEntry[];
}

export type CashGameVariantLine = { bankrollId?: string; variant: string; stakes: string; tableType?: string | null; handsCount?: string | number | null; profit?: string | number | null };

interface SessionData {
  format?: string;
  universe?: 'Online' | 'Live';
  date?: Date;
  duration?: string;
  tournamentCount?: string;
  buyInTotal?: string;
  /** Spin & Go : niveau de buy-in (5, 10, 20, ...) */
  buyInLevel?: string;
  stakes?: string;
  customStakes?: string;
  tableType?: string;
  variant?: string;
  customVariant?: string;
  handsCount?: string;
  /** Cash Game : une ligne par variante (limite, type, nombre de mains) */
  cashGameVariantLines?: CashGameVariantLine[] | null;
  bankrolls?: Map<string, SelectedBankroll>;
  notes?: string;
  /** MTT Live sans bankroll : résultat net (ex: gains ou perte) */
  sessionProfit?: string;
  sessionProfitCurrency?: string;
  /** MTT Live sans bankroll : nom du tournoi */
  tournamentName?: string;
  /** MTT Live sans bankroll : ITM ou Eliminé */
  mttLiveResultType?: 'ITM' | 'Eliminated';
  /** MTT Live sans bankroll : nombre de re-entries (0 = aucune) */
  mttLiveEntryCount?: string;
  /** MTT Live : festival auquel rattacher la session (optionnel, id UserFestival) */
  userFestivalId?: string | null;
  /** MTT Live : festival catalogue choisi (si pas encore dans la liste utilisateur) */
  pokerFestivalId?: string | null;
  /** MTT Live : tournoi catalogue choisi */
  pokerTournamentId?: string | null;
  /** MTT Live : tournoi utilisateur déjà existant */
  userTournamentId?: string | null;
  /** Session poker room (Cash Game Live) */
  cashLiveRoomId?: string;
  cashLiveRoomName?: string;
  cashLiveDurationMinutes?: number | null;
  cashLiveCurrency?: string;
  cashLiveLimits?: string | null;
  cashLiveVariant?: string | null;
  cashLiveBuyIn?: number;
  cashLiveCashout?: number;
  cashLiveNotes?: string | null;
}

interface NewSessionStep2DetailsProps {
  onComplete: (data: Partial<SessionData>) => void;
  sessionData: SessionData;
  /** Appelé avant la navigation vers /bankrolls (ex: fermer la modale) */
  onClose?: () => void;
}

export const NewSessionStep2Details: React.FC<NewSessionStep2DetailsProps> = ({ 
  onComplete, 
  sessionData,
  onClose 
}) => {
  const t = useTranslations('sessions.new.manual');
  const appLocale = useLocale();
  const dateFnsLocale = DATE_FNS_BY_APP_LOCALE[appLocale] ?? fr;
  const tStep1 = useTranslations('sessions.new.stepper.step1');
  const tStep2 = useTranslations('sessions.new.stepper.step2');
  const tStepper = useTranslations('sessions.new.stepper.step2LimitsVariants');
  const tAuthSignUp = useTranslations('auth.signUp');
  const { nextStep, previousStep } = useStepperContext();
  const setFooter = useSessionFooter()?.setFooter;
  
  const format = sessionData.format || 'MTT';
  const universe = sessionData.universe || 'Online';
  
  const durationFromMinutes = minutesToHoursMinutes(sessionData.duration ? parseInt(sessionData.duration, 10) : null);
  const [durationHours, setDurationHours] = useState(durationInputValue(durationFromMinutes.hours));
  const [durationMinutes, setDurationMinutes] = useState(durationInputValue(durationFromMinutes.minutes));
  const [tournamentCount, setTournamentCount] = useState(sessionData.tournamentCount || '');
  const [buyInTotal, setBuyInTotal] = useState(sessionData.buyInTotal || '');
  const [buyInLevel, setBuyInLevel] = useState(sessionData.buyInLevel || '');
  const [notes, setNotes] = useState(sessionData.notes || '');
  const [sessionProfit, setSessionProfit] = useState(sessionData.sessionProfit ?? '');
  const [sessionProfitCurrency, setSessionProfitCurrency] = useState(sessionData.sessionProfitCurrency || 'EUR');
  const [tournamentName, setTournamentName] = useState(sessionData.tournamentName || '');
  const [mttLiveResultType, setMttLiveResultType] = useState<'ITM' | 'Eliminated' | ''>(sessionData.mttLiveResultType || '');
  const [mttLiveEntryCount, setMttLiveEntryCount] = useState(sessionData.mttLiveEntryCount ?? '0');
  const [userFestivalId, setUserFestivalId] = useState<string | null>(sessionData.userFestivalId ?? null);
  const [userFestivals, setUserFestivals] = useState<UserFestivalOption[]>([]);
  const [catalogFestivals, setCatalogFestivals] = useState<PokerFestivalOption[]>([]);
  const [pokerFestivalId, setPokerFestivalId] = useState<string | null>(sessionData.pokerFestivalId ?? null);
  const [pokerTournamentId, setPokerTournamentId] = useState<string | null>(sessionData.pokerTournamentId ?? null);
  const [userTournamentId, setUserTournamentId] = useState<string | null>(sessionData.userTournamentId ?? null);
  const [tournamentOptions, setTournamentOptions] = useState<TournamentOption[]>([]);
  const [catalogFestivalCurrency, setCatalogFestivalCurrency] = useState<string | null>(null);
  /** Filtre texte sur la liste des festivals (MTT Live). */
  const [festivalFilter, setFestivalFilter] = useState('');
  const [festivalListOpen, setFestivalListOpen] = useState(true);
  const [bankrolls, setBankrolls] = useState<Bankroll[]>([]);
  const [selectedBankrolls, setSelectedBankrolls] = useState<Map<string, SelectedBankroll>>(
    sessionData.bankrolls || new Map()
  );
  const [bankrollsRefreshKey, setBankrollsRefreshKey] = useState(0);

  const isCashGameLive = format === 'Cash Game' && universe === 'Live';
  const [cashLiveRooms, setCashLiveRooms] = useState<
    { id: string; name: string; city?: string | null; currency?: string | null; roomCurrency?: string | null }[]
  >([]);
  const [cashLiveRoomId, setCashLiveRoomId] = useState(sessionData.cashLiveRoomId || '');
  const [cashLiveDurationHours, setCashLiveDurationHours] = useState('');
  const [cashLiveDurationMinutes, setCashLiveDurationMinutes] = useState('');
  const [cashLiveCurrency, setCashLiveCurrency] = useState(sessionData.cashLiveCurrency || 'EUR');
  const [cashLiveLimits, setCashLiveLimits] = useState(sessionData.cashLiveLimits || '1/2');
  const [cashLiveVariant, setCashLiveVariant] = useState(sessionData.cashLiveVariant || "No Limit Hold'em");
  const [cashLiveBuyIn, setCashLiveBuyIn] = useState(sessionData.cashLiveBuyIn != null ? String(sessionData.cashLiveBuyIn) : '');
  const [cashLiveCashout, setCashLiveCashout] = useState(sessionData.cashLiveCashout != null ? String(sessionData.cashLiveCashout) : '');
  const [cashLiveNotes, setCashLiveNotes] = useState(sessionData.cashLiveNotes || '');
  const selectedCashLiveRoom = cashLiveRoomId ? cashLiveRooms.find((r) => r.id === cashLiveRoomId) ?? null : null;
  const lockedCashLiveRoomCurrency = selectedCashLiveRoom?.roomCurrency?.trim() || selectedCashLiveRoom?.currency?.trim() || null;
  const isCashLiveCurrencyLocked = isCashGameLive && !!lockedCashLiveRoomCurrency;

  /** Autorise uniquement chiffres et une virgule/point (nombre positif) */
  const sanitizePositiveNumber = (value: string): string => {
    const noMinus = value.replace(/-/g, '');
    const normalized = noMinus.replace(',', '.');
    const digitsAndDot = normalized.replace(/[^0-9.]/g, '');
    const parts = digitsAndDot.split('.');
    if (parts.length <= 1) return digitsAndDot;
    return parts[0] + '.' + parts.slice(1).join('');
  };

  useEffect(() => {
    const handleBankrollsUpdated = () => setBankrollsRefreshKey((k) => k + 1);
    window.addEventListener('bankrollsUpdated', handleBankrollsUpdated);
    return () => window.removeEventListener('bankrollsUpdated', handleBankrollsUpdated);
  }, []);

  useEffect(() => {
    const fetchBankrolls = async () => {
      try {
        const response = await fetch('/api/bankrolls');
        const data = await response.json();
        if (data.bankrolls) {
          let filtered = data.bankrolls;
          // MTT, Spin & Go : filtrer selon l'univers (Online/Live)
          if (format === 'MTT' || format === 'Spin & Go' || format === 'Spin&Go') {
            filtered = data.bankrolls.filter((b: Bankroll) => 
              universe === 'Online' ? b.type === 'Online' : b.type === 'Live'
            );
          } else if (format === 'Cash Game') {
            // Cash Game : afficher uniquement les bankrolls de l'univers (Online ou Live)
            filtered = data.bankrolls.filter((b: Bankroll) => 
              universe === 'Online' ? b.type === 'Online' : b.type === 'Live'
            );
          }
          setBankrolls(filtered);
        }
      } catch (err) {
        console.error('Error fetching bankrolls:', err);
      }
    };

    fetchBankrolls();
  }, [universe, format, bankrollsRefreshKey]);

  useEffect(() => {
    if (!isCashGameLive) return;
    let cancelled = false;
    fetch('/api/cash-live/rooms')
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data?.rooms) return;
        setCashLiveRooms(
          data.rooms.map(
            (r: { id: string; name: string; city?: string | null; currency?: string | null; roomCurrency?: string | null }) => ({
              id: r.id,
              name: r.name,
              city: r.city,
              currency: r.currency ?? null,
              roomCurrency: r.roomCurrency ?? null,
            })
          )
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isCashGameLive]);

  /** Cash Game Live: si une room est sélectionnée et qu'elle porte une devise, la devise session est verrouillée dessus. */
  useEffect(() => {
    if (!isCashGameLive) return;
    if (!lockedCashLiveRoomCurrency) return;
    setCashLiveCurrency(lockedCashLiveRoomCurrency);
  }, [isCashGameLive, lockedCashLiveRoomCurrency]);

  // MTT Live : charger les festivals utilisateur + catalogue global
  useEffect(() => {
    if (format !== 'MTT' || universe !== 'Live') return;
    let cancelled = false;
    Promise.all([
      fetch('/api/user/festivals').then((r) => r.json()).catch(() => null),
      fetch('/api/poker-festivals?limit=500').then((r) => r.json()).catch(() => null),
    ])
      .then(([userData, catalogData]) => {
        if (cancelled) return;
        const userList = (userData?.userFestivals as {
          id: string;
          pokerFestivalId?: string | null;
          pokerFestival?: {
            name: string;
            startDate?: string | Date | null;
            endDate?: string | Date | null;
            venue?: string | null;
          } | null;
          customName?: string | null;
          customStartDate?: string | Date | null;
          customEndDate?: string | Date | null;
          customVenue?: string | null;
          customCurrency?: string | null;
          festivalCurrency?: string | null;
          countryCode?: string | null;
          userTournaments?: Array<{
            id: string;
            customName?: string | null;
            customBuyInAmount?: number | string | null;
            pokerTournamentId?: string | null;
            pokerTournament?: { name?: string | null; buyInAmount?: number | string | null } | null;
          }>;
        }[] | undefined)?.map((uf) => ({
          id: uf.id,
          pokerFestivalId: uf.pokerFestivalId?.trim() || null,
          name: uf.pokerFestival?.name ?? uf.customName ?? uf.id,
          currency: uf.festivalCurrency?.trim() || uf.customCurrency?.trim() || null,
          startDate: uf.pokerFestival?.startDate ?? uf.customStartDate ?? null,
          endDate: uf.pokerFestival?.endDate ?? uf.customEndDate ?? null,
          venue: uf.pokerFestival?.venue?.trim() || uf.customVenue?.trim() || null,
          countryCode: uf.countryCode?.trim() || null,
          userTournaments: (uf.userTournaments ?? []).map((ut) => ({
            id: ut.id,
            pokerTournamentId: ut.pokerTournamentId?.trim() || null,
            name: ut.pokerTournament?.name?.trim() || ut.customName?.trim() || 'Tournoi',
            buyInAmount:
              ut.pokerTournament?.buyInAmount != null
                ? Number(ut.pokerTournament.buyInAmount)
                : ut.customBuyInAmount != null
                  ? Number(ut.customBuyInAmount)
                  : null,
          })),
        })) ?? [];
        const catalogList =
          (
            catalogData?.festivals as
              | {
                  id: string;
                  name: string;
                  startDate?: string | Date | null;
                  endDate?: string | Date | null;
                  venue?: string | null;
                  countryCode?: string | null;
                }[]
              | undefined
          )?.map((f) => ({
            id: f.id,
            name: f.name,
            startDate: f.startDate ?? null,
            endDate: f.endDate ?? null,
            venue: f.venue?.trim() || null,
            countryCode: f.countryCode?.trim() || null,
          })) ?? [];
        setUserFestivals(userList);
        setCatalogFestivals(catalogList);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [format, universe]);

  useEffect(() => {
    if (format !== 'MTT' || universe !== 'Live') setFestivalFilter('');
  }, [format, universe]);

  const selectedUserFestival = userFestivalId
    ? userFestivals.find((uf) => uf.id === userFestivalId) ?? null
    : null;
  const selectedFestivalPokerId = selectedUserFestival?.pokerFestivalId ?? pokerFestivalId ?? null;

  useEffect(() => {
    if (format !== 'MTT' || universe !== 'Live') return;
    if (!selectedFestivalPokerId) {
      setCatalogFestivalCurrency(null);
      setTournamentOptions(
        (selectedUserFestival?.userTournaments ?? []).map((ut) => ({
          value: `user:${ut.id}`,
          label: ut.name,
          userTournamentId: ut.id,
          pokerTournamentId: ut.pokerTournamentId,
          buyInAmount: ut.buyInAmount,
        }))
      );
      return;
    }
    let cancelled = false;
    fetch(`/api/poker-festivals/${selectedFestivalPokerId}/tournaments`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const firstFestivalCurrency =
          (data?.tournaments as { buyInCurrency?: string | null }[] | undefined)
            ?.map((t) => t.buyInCurrency?.trim())
            .find((cur): cur is string => !!cur) ?? null;
        setCatalogFestivalCurrency(firstFestivalCurrency);
        const userOptions = (selectedUserFestival?.userTournaments ?? []).map((ut) => ({
          value: `user:${ut.id}`,
          label: ut.name,
          userTournamentId: ut.id,
          pokerTournamentId: ut.pokerTournamentId,
          buyInAmount: ut.buyInAmount,
        }));
        const userPokerIds = new Set(
          userOptions.map((opt) => opt.pokerTournamentId).filter((id): id is string => !!id)
        );
        const catalogOptions = (data?.tournaments as { id: string; name: string; buyInAmount?: number | string | null }[] | undefined)?.filter((t) => !userPokerIds.has(t.id)).map((t) => ({
          value: `catalog:${t.id}`,
          label: t.name,
          userTournamentId: null,
          pokerTournamentId: t.id,
          buyInAmount: t.buyInAmount != null ? Number(t.buyInAmount) : null,
        })) ?? [];
        setTournamentOptions([...userOptions, ...catalogOptions]);
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogFestivalCurrency(null);
          setTournamentOptions(
            (selectedUserFestival?.userTournaments ?? []).map((ut) => ({
              value: `user:${ut.id}`,
              label: ut.name,
              userTournamentId: ut.id,
              pokerTournamentId: ut.pokerTournamentId,
              buyInAmount: ut.buyInAmount,
            }))
          );
        }
      });
    return () => { cancelled = true; };
  }, [format, universe, selectedFestivalPokerId, selectedUserFestival]);

  /** MTT Live: si un festival est sélectionné et qu'il a une devise, on verrouille la devise tournoi dessus. */
  useEffect(() => {
    if (!(format === 'MTT' && universe === 'Live')) return;
    const festivalCurrency = selectedUserFestival?.currency?.trim() || catalogFestivalCurrency?.trim() || null;
    if (festivalCurrency) {
      setSessionProfitCurrency(festivalCurrency);
    }
  }, [format, universe, selectedUserFestival, catalogFestivalCurrency]);

  // Pré-remplir newBalance pour les bankrolls pré-sélectionnées (ex: depuis Cash Live) quand la liste est chargée
  useEffect(() => {
    if (bankrolls.length === 0) return;
    let updated = false;
    const next = new Map(selectedBankrolls);
    next.forEach((entry, bankrollId) => {
      if (entry.newBalance === '' || entry.newBalance === undefined) {
        const b = bankrolls.find((x: Bankroll) => x.id === bankrollId);
        if (b?.currentBalance != null) {
          next.set(bankrollId, { ...entry, newBalance: b.currentBalance });
          updated = true;
        }
      }
    });
    if (updated) setSelectedBankrolls(next);
  }, [bankrolls]);

  useEffect(() => {
    const parsed = sessionData.duration ? parseInt(sessionData.duration, 10) : null;
    const { hours, minutes } = minutesToHoursMinutes(parsed);
    setDurationHours(durationInputValue(hours));
    setDurationMinutes(durationInputValue(minutes));
  }, [sessionData.duration]);

  // Spin & Go : une seule bankroll — si l'état contenait plusieurs entrées (ex. retour arrière), n'en garder qu'une
  const isSpinGoFormat = format === 'Spin & Go' || format === 'Spin&Go';
  useEffect(() => {
    if (!isSpinGoFormat || selectedBankrolls.size <= 1) return;
    setSelectedBankrolls((prev) => {
      const firstKey = prev.keys().next().value as string | undefined;
      if (!firstKey) return prev;
      const entry = prev.get(firstKey);
      return entry ? new Map([[firstKey, entry]]) : new Map();
    });
  }, [isSpinGoFormat, selectedBankrolls.size]);

  const handleBankrollToggle = (bankrollId: string) => {
    const isSpinGo = format === 'Spin & Go' || format === 'Spin&Go';
    const newSelected = new Map(selectedBankrolls);
    if (newSelected.has(bankrollId)) {
      newSelected.delete(bankrollId);
    } else {
      const bankroll = bankrolls.find(b => b.id === bankrollId);
      if (bankroll) {
        // Spin & Go : une seule bankroll (l'étape « Sessions » est par room)
        if (isSpinGo) {
          newSelected.clear();
        }
        const entry: SelectedBankroll = {
          bankrollId,
          newBalance: bankroll.currentBalance,
          rackback: '',
        };
        // MTT Online : ne pas pré-remplir Tournois / Buy-in total pour que la détection "champs manquants" fonctionne
        if (format === 'MTT' && universe === 'Online') {
          entry.tournamentCount = '';
          entry.buyInTotal = '';
        }
        // Spin & Go : pas de spinGoDetails ici, ils seront saisis à l'étape "Sessions"
        if (!isSpinGo) {
          // (spinGoDetails n'est plus rempli en étape Détails)
        }
        newSelected.set(bankrollId, entry);
      }
    }
    setSelectedBankrolls(newSelected);
  };

  const handleNewBalanceChange = (bankrollId: string, value: string) => {
    const newSelected = new Map(selectedBankrolls);
    const selected = newSelected.get(bankrollId);
    if (selected) {
      newSelected.set(bankrollId, { ...selected, newBalance: value });
      setSelectedBankrolls(newSelected);
    }
  };

  const handleMTTBankrollFieldChange = (bankrollId: string, field: 'tournamentCount' | 'buyInTotal', value: string) => {
    const newSelected = new Map(selectedBankrolls);
    const selected = newSelected.get(bankrollId);
    if (selected) {
      // Tournois : uniquement des entiers >= 0 (pas de négatif ni décimal)
      const normalized =
        field === 'tournamentCount' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value;
      newSelected.set(bankrollId, { ...selected, [field]: normalized });
      setSelectedBankrolls(newSelected);
    }
  };

  const handleRackbackChange = (bankrollId: string, value: string) => {
    const newSelected = new Map(selectedBankrolls);
    const selected = newSelected.get(bankrollId);
    if (selected) {
      newSelected.set(bankrollId, {
        ...selected,
        rackback: value,
      });
      setSelectedBankrolls(newSelected);
    }
  };

  const isMTTLiveNoBankroll = format === 'MTT' && universe === 'Live';
  const doSubmit = useCallback(() => {
    const spinGo = format === 'Spin & Go' || format === 'Spin&Go';
    const isCashGame = format === 'Cash Game';
    const totalMinutes = hoursMinutesToMinutes(parseInt(durationHours, 10) || 0, parseInt(durationMinutes, 10) || 0);
    const isMTT = format === 'MTT';
    const data: Partial<SessionData> = {
      date: sessionData.date,
      ...(isCashGame ? {} : { notes }),
      ...(!isMTT ? { duration: totalMinutes > 0 ? String(totalMinutes) : undefined, tournamentCount, buyInTotal } : { tournamentCount, buyInTotal }),
      ...(isMTT && (isMTTLiveNoBankroll || universe === 'Online') && totalMinutes > 0 && { duration: String(totalMinutes) }),
      buyInLevel: spinGo ? undefined : buyInLevel || undefined,
      bankrolls: selectedBankrolls,
      ...(isMTTLiveNoBankroll && {
          // En ITM on garde les gains bruts (cashout) pour que "Précédent" réaffiche les bonnes valeurs ; le net est calculé à l'étape 3.
          sessionProfit:
            mttLiveResultType === 'ITM'
              ? (sessionProfit.trim() || undefined)
              : mttLiveResultType === 'Eliminated'
                ? (() => {
                  const buyIn = parseFloat(buyInTotal.replace(',', '.')) || 0;
                  const reEntry = parseInt(mttLiveEntryCount, 10) || 0;
                  const total = buyIn * (1 + reEntry);
                  return total > 0 ? String(-total) : undefined;
                })()
                : undefined,
          sessionProfitCurrency: sessionProfitCurrency || 'EUR',
          tournamentName: tournamentName.trim() || undefined,
          mttLiveResultType: mttLiveResultType || undefined,
          mttLiveEntryCount: mttLiveEntryCount.trim() || undefined,
          userFestivalId: userFestivalId && userFestivalId.trim() !== '' ? userFestivalId : undefined,
          pokerFestivalId: !userFestivalId && pokerFestivalId && pokerFestivalId.trim() !== '' ? pokerFestivalId : undefined,
          pokerTournamentId: pokerTournamentId && pokerTournamentId.trim() !== '' ? pokerTournamentId : undefined,
          userTournamentId: userTournamentId && userTournamentId.trim() !== '' ? userTournamentId : undefined,
        }),
    };
    if (isCashGameLive) {
      const buyInNum = cashLiveBuyIn.trim() ? parseFloat(cashLiveBuyIn.replace(',', '.')) : NaN;
      const cashoutNum = cashLiveCashout.trim() ? parseFloat(cashLiveCashout.replace(',', '.')) : NaN;
      const durMins = (parseInt(cashLiveDurationHours, 10) || 0) * 60 + (parseInt(cashLiveDurationMinutes, 10) || 0);
      const room = cashLiveRooms.find((r) => r.id === cashLiveRoomId);
      Object.assign(data, {
        cashLiveRoomId: cashLiveRoomId || undefined,
        cashLiveRoomName: room?.name,
        cashLiveDurationMinutes: durMins > 0 ? durMins : null,
        cashLiveCurrency: cashLiveCurrency || 'EUR',
        cashLiveLimits: cashLiveLimits || null,
        cashLiveVariant: cashLiveVariant || null,
        cashLiveBuyIn: !isNaN(buyInNum) ? buyInNum : undefined,
        cashLiveCashout: !isNaN(cashoutNum) ? cashoutNum : undefined,
        cashLiveNotes: cashLiveNotes.trim() || null,
      });
    }
    onComplete(data);
    nextStep();
  }, [sessionData.date, durationHours, durationMinutes, tournamentCount, buyInTotal, buyInLevel, format, universe, selectedBankrolls, notes, sessionProfit, sessionProfitCurrency, tournamentName, mttLiveResultType, mttLiveEntryCount, userFestivalId, pokerFestivalId, pokerTournamentId, userTournamentId, isMTTLiveNoBankroll, isCashGameLive, cashLiveRoomId, cashLiveRooms, cashLiveDurationHours, cashLiveDurationMinutes, cashLiveCurrency, cashLiveLimits, cashLiveVariant, cashLiveBuyIn, cashLiveCashout, cashLiveNotes, onComplete, nextStep]);

  const handleContinue = useCallback(() => {
    const date = sessionData.date;
    if (!date) return;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (date > today) return;
    doSubmit();
  }, [sessionData.date, doSubmit]);

  const isContinueDisabled =
    !sessionData.date ||
    (sessionData.date && sessionData.date > new Date(new Date().setHours(23, 59, 59, 999))) ||
    (isCashGameLive
      ? !cashLiveBuyIn.trim() || !cashLiveCashout.trim() ||
        isNaN(parseFloat(cashLiveBuyIn.replace(',', '.'))) || isNaN(parseFloat(cashLiveCashout.replace(',', '.')))
      : isMTTLiveNoBankroll
        ? buyInTotal.trim() === '' || mttLiveResultType === '' || (mttLiveResultType === 'ITM' && sessionProfit.trim() === '')
        : selectedBankrolls.size === 0);

  useEffect(() => {
    if (!setFooter) return;
setFooter({
        secondary: {
          label: tStepper('previous'),
          onClick: previousStep,
        },
        primary: {
          label: tStepper('continue'),
          disabled: isContinueDisabled,
          onClick: handleContinue,
        },
      });
    return () => setFooter(null);
  }, [setFooter, tStepper, isContinueDisabled, handleContinue, previousStep]);

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const isSpinGo = format === 'Spin & Go' || format === 'Spin&Go';
  const isMTT = format === 'MTT';
  const userPokerFestivalIds = useMemo(
    () =>
      new Set(
        userFestivals
          .map((uf) => uf.pokerFestivalId)
          .filter((id): id is string => !!id)
      ),
    [userFestivals]
  );
  const otherCatalogFestivals = useMemo(
    () => catalogFestivals.filter((f) => !userPokerFestivalIds.has(f.id)),
    [catalogFestivals, userPokerFestivalIds]
  );

  const festivalFilterNorm = useMemo(() => normalizeFestivalSearch(festivalFilter), [festivalFilter]);
  const filteredUserFestivals = useMemo(() => {
    if (!festivalFilterNorm) return userFestivals;
    return userFestivals.filter((uf) => normalizeFestivalSearch(uf.name).includes(festivalFilterNorm));
  }, [userFestivals, festivalFilterNorm]);
  const filteredCatalogFestivals = useMemo(() => {
    if (!festivalFilterNorm) return otherCatalogFestivals;
    return otherCatalogFestivals.filter((f) => normalizeFestivalSearch(f.name).includes(festivalFilterNorm));
  }, [otherCatalogFestivals, festivalFilterNorm]);

  const selectedTournamentValue = userTournamentId
    ? `user:${userTournamentId}`
    : pokerTournamentId
      ? `catalog:${pokerTournamentId}`
      : '';
  const selectedTournament = selectedTournamentValue
    ? tournamentOptions.find((opt) => opt.value === selectedTournamentValue) ?? null
    : null;
  const isBuyInLockedByTournament = isMTTLiveNoBankroll && !!selectedTournamentValue;
  const lockedFestivalCurrency = selectedUserFestival?.currency?.trim() || catalogFestivalCurrency?.trim() || null;
  const isTournamentCurrencyLocked = isMTTLiveNoBankroll && !!lockedFestivalCurrency;

  const selectedFestivalRaw = userFestivalId
    ? `user:${userFestivalId}`
    : pokerFestivalId
      ? `catalog:${pokerFestivalId}`
      : '';

  const applyFestivalSelection = useCallback((raw: string) => {
    setPokerTournamentId(null);
    setUserTournamentId(null);
    if (!raw.trim()) {
      setUserFestivalId(null);
      setPokerFestivalId(null);
      setFestivalFilter('');
    } else if (raw.startsWith('user:')) {
      const nextUserFestivalId = raw.slice('user:'.length);
      const uf = userFestivals.find((f) => f.id === nextUserFestivalId) ?? null;
      setUserFestivalId(nextUserFestivalId || null);
      setPokerFestivalId(uf?.pokerFestivalId ?? null);
      setFestivalFilter(uf?.name ?? '');
    } else if (raw.startsWith('catalog:')) {
      const nextPokerFestivalId = raw.slice('catalog:'.length);
      setUserFestivalId(null);
      setPokerFestivalId(nextPokerFestivalId || null);
      const cf = catalogFestivals.find((f) => f.id === nextPokerFestivalId) ?? null;
      setFestivalFilter(cf?.name ?? '');
    }
    setFestivalListOpen(false);
  }, [userFestivals, catalogFestivals]);

  useEffect(() => {
    if (format !== 'MTT' || universe !== 'Live') return;
    if (!userFestivalId && !pokerFestivalId) return;
    const name =
      (userFestivalId ? userFestivals.find((uf) => uf.id === userFestivalId)?.name : null) ??
      (pokerFestivalId ? catalogFestivals.find((f) => f.id === pokerFestivalId)?.name : null);
    if (!name) return;
    setFestivalFilter((prev) => (prev.trim() === '' ? name : prev));
  }, [format, universe, userFestivalId, pokerFestivalId, userFestivals, catalogFestivals]);

  const festivalListNoResults =
    festivalFilterNorm.length > 0 &&
    filteredUserFestivals.length === 0 &&
    filteredCatalogFestivals.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {t('sessionInfo.title')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {tStep2('subtitle')}
        </p>
      </div>

      {/* MTT Live : Tournoi — Nom du tournoi, Festival (optionnel), Devise, Buy-in, Re-entry, Durée, Résultat */}
      {isMTTLiveNoBankroll && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Trophy className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <span className="text-sm font-medium">Tournoi</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Festival (optionnel) — uniquement les festivals sélectionnés par l'utilisateur */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('festivalOptional')}</label>
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                <Input
                  type="search"
                  autoComplete="off"
                  value={festivalFilter}
                  onChange={(e) => setFestivalFilter(e.target.value)}
                  onFocus={() => setFestivalListOpen(true)}
                  placeholder={t('festivalSearchPlaceholder')}
                  className="rounded-lg border border-gray-200 bg-white pl-9 dark:border-gray-600 dark:bg-gray-800"
                  aria-label={t('festivalSearchPlaceholder')}
                  aria-expanded={festivalListOpen}
                  aria-controls={festivalListOpen ? 'new-session-festival-listbox' : undefined}
                />
              </div>
              {festivalListOpen ? (
              <div
                id="new-session-festival-listbox"
                role="listbox"
                aria-label={t('festivalOptional')}
                className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm"
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedFestivalRaw === ''}
                  onClick={() => {
                    applyFestivalSelection('');
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/80 border-b border-gray-100 dark:border-gray-700',
                    selectedFestivalRaw === '' && 'bg-gray-50 dark:bg-gray-700/60 font-medium'
                  )}
                >
                  {t('noFestival')}
                </button>
                {filteredUserFestivals.length > 0 ? (
                  <>
                    <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                      {t('festivalGroupMine')}
                    </div>
                    {filteredUserFestivals.map((uf) => {
                      const raw = `user:${uf.id}`;
                      const selected = selectedFestivalRaw === raw;
                      const { datePart, venuePart } = festivalSubtitleParts(
                        uf.startDate,
                        uf.endDate,
                        uf.venue,
                        dateFnsLocale
                      );
                      return (
                        <button
                          key={uf.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => applyFestivalSelection(raw)}
                          className={cn(
                            'flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/80',
                            selected && 'bg-amber-50 dark:bg-amber-950/30'
                          )}
                        >
                          <FestivalFlag countryCode={uf.countryCode} />
                          <div className="min-w-0 flex-1">
                            <div
                              className={cn(
                                'text-sm text-gray-900 dark:text-white',
                                selected && 'font-semibold'
                              )}
                            >
                              {uf.name}
                            </div>
                            {datePart || venuePart ? (
                              <div className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">
                                {datePart}
                                {datePart && venuePart ? ' · ' : null}
                                {venuePart}
                              </div>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </>
                ) : null}
                {filteredCatalogFestivals.length > 0 ? (
                  <>
                    <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                      {t('festivalGroupOther')}
                    </div>
                    {filteredCatalogFestivals.map((festival) => {
                      const raw = `catalog:${festival.id}`;
                      const selected = selectedFestivalRaw === raw;
                      const { datePart, venuePart } = festivalSubtitleParts(
                        festival.startDate,
                        festival.endDate,
                        festival.venue,
                        dateFnsLocale
                      );
                      return (
                        <button
                          key={festival.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => applyFestivalSelection(raw)}
                          className={cn(
                            'flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/80',
                            selected && 'bg-amber-50 dark:bg-amber-950/30'
                          )}
                        >
                          <FestivalFlag countryCode={festival.countryCode} />
                          <div className="min-w-0 flex-1">
                            <div
                              className={cn(
                                'text-sm text-gray-900 dark:text-white',
                                selected && 'font-semibold'
                              )}
                            >
                              {festival.name}
                            </div>
                            {datePart || venuePart ? (
                              <div className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">
                                {datePart}
                                {datePart && venuePart ? ' · ' : null}
                                {venuePart}
                              </div>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </>
                ) : null}
                {festivalListNoResults ? (
                  <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">{t('festivalSearchNoResults')}</div>
                ) : null}
              </div>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Tournoi (optionnel)</label>
              <select
                value={selectedTournamentValue}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  if (!raw) {
                    setUserTournamentId(null);
                    setPokerTournamentId(null);
                    return;
                  }
                  const option = tournamentOptions.find((opt) => opt.value === raw);
                  if (!option) return;
                  setUserTournamentId(option.userTournamentId);
                  setPokerTournamentId(option.pokerTournamentId);
                  setTournamentName(option.label);
                  if (option.buyInAmount != null && Number.isFinite(option.buyInAmount)) {
                    setBuyInTotal(String(option.buyInAmount));
                  }
                }}
                disabled={!userFestivalId && !pokerFestivalId}
                className={cn(
                  "w-full h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white",
                  !userFestivalId && !pokerFestivalId && "opacity-70 cursor-not-allowed"
                )}
              >
                <option value="">
                  {!userFestivalId && !pokerFestivalId
                    ? 'Sélectionnez d’abord un festival'
                    : 'Aucun tournoi'}
                </option>
                {tournamentOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.userTournamentId ? `Mes tournois - ${option.label}` : option.label}
                  </option>
                ))}
              </select>
            </div>
            {/* 1. Nom du tournoi | Devise du tournoi */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Nom du tournoi</label>
              <Input type="text" value={tournamentName} onChange={(e) => setTournamentName(e.target.value)} placeholder="Ex: Main Event, Side event..." className="rounded-lg border border-gray-200 dark:border-gray-600" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Devise du tournoi</label>
              <select
                value={sessionProfitCurrency}
                onChange={(e) => setSessionProfitCurrency(e.target.value)}
                disabled={isTournamentCurrencyLocked}
                className={cn(
                  "w-full h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white",
                  isTournamentCurrencyLocked && "opacity-70 cursor-not-allowed"
                )}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              {isTournamentCurrencyLocked ? (
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Devise verrouillée par le festival ({lockedFestivalCurrency})
                </p>
              ) : null}
            </div>
            {/* 2. Buy-in | Re-entry */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Buy-in *</label>
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={buyInTotal}
                  onChange={(e) => setBuyInTotal(e.target.value)}
                  placeholder="0"
                  disabled={isBuyInLockedByTournament}
                  className={cn(
                    "border-0 focus-visible:ring-0 rounded-none",
                    isBuyInLockedByTournament && "opacity-70 cursor-not-allowed"
                  )}
                />
                <span className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 shrink-0">{sessionProfitCurrency}</span>
              </div>
              {isBuyInLockedByTournament ? (
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Buy-in verrouillé par le tournoi{selectedTournament?.buyInAmount != null ? ` (${selectedTournament.buyInAmount} ${sessionProfitCurrency})` : ''}
                </p>
              ) : null}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Re-entry</label>
              <Input type="number" min="0" value={mttLiveEntryCount} onChange={(e) => setMttLiveEntryCount(e.target.value)} placeholder="0" className="rounded-lg border border-gray-200 dark:border-gray-600" />
            </div>
            {/* 3. Durée (optionnel) — pleine largeur */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('sessionInfo.duration')} ({tAuthSignUp('optional')})</label>
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 overflow-hidden w-24">
                  <Input type="number" min="0" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} placeholder="0" className="border-0 bg-transparent text-center h-9 w-12 px-1" />
                  <span className="text-xs text-gray-500 shrink-0">h</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 overflow-hidden w-24">
                  <Input type="number" min="0" max="59" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} placeholder="0" className="border-0 bg-transparent text-center h-9 w-12 px-1" />
                  <span className="text-xs text-gray-500 shrink-0">min</span>
                </div>
              </div>
            </div>
            {/* 4. Résultat * — Eliminé / ITM et montant sur une même ligne (perte affichée ou gains saisis) */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Résultat *</label>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="mttLiveResult" value="Eliminated" checked={mttLiveResultType === 'Eliminated'} onChange={() => setMttLiveResultType('Eliminated')} className="text-amber-500 border-gray-300 focus:ring-amber-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Eliminé</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="mttLiveResult" value="ITM" checked={mttLiveResultType === 'ITM'} onChange={() => setMttLiveResultType('ITM')} className="text-amber-500 border-gray-300 focus:ring-amber-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">ITM</span>
                  </label>
                </div>
                {mttLiveResultType === 'Eliminated' ? (
                  <div className="flex items-center gap-2 h-10 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
                    <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                      −{(() => {
                        const buyIn = parseFloat(buyInTotal.replace(',', '.')) || 0;
                        const reEntry = parseInt(mttLiveEntryCount, 10) || 0;
                        const total = buyIn * (1 + reEntry);
                        return isNaN(total) ? '0' : total.toFixed(0);
                      })()} {sessionProfitCurrency}
                    </span>
                  </div>
                ) : mttLiveResultType === 'ITM' ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400 shrink-0">Gains bruts (cashout) :</span>
                      <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden w-28">
                        <Input type="number" step="0.01" min="0" value={sessionProfit} onChange={(e) => setSessionProfit(sanitizePositiveNumber(e.target.value))} placeholder="0" className={cn('border-0 focus-visible:ring-0 rounded-none font-medium placeholder:text-gray-400', (parseFloat(sessionProfit.replace(',', '.')) || 0) >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400')} />
                        <span className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 shrink-0">{sessionProfitCurrency}</span>
                      </div>
                    </div>
                    {(() => {
                      const buyIn = parseFloat(buyInTotal.replace(',', '.')) || 0;
                      const reEntry = parseInt(mttLiveEntryCount, 10) || 0;
                      const totalBuyIn = buyIn * (1 + reEntry);
                      const gross = parseFloat(sessionProfit.replace(',', '.')) || 0;
                      const net = totalBuyIn > 0 || gross > 0 ? gross - totalBuyIn : null;
                      if (net === null || (reEntry === 0 && totalBuyIn === 0)) return null;
                      return (
                        <span className={cn('text-sm font-medium', net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                          → Net : {net >= 0 ? '+' : ''}{net.toFixed(0)} {sessionProfitCurrency}
                        </span>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Choisissez d&apos;abord un résultat
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Game Live : salle, durée, limites, variante, buy-in, cashout */}
      {isCashGameLive && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Building2 className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <span className="text-sm font-medium">{tStep1('liveSessionRoom')}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Poker Room ({tAuthSignUp('optional')})</label>
            <select
              value={cashLiveRoomId}
              onChange={(e) => setCashLiveRoomId(e.target.value)}
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              <option value="">Aucune poker room (regroupées dans « Sessions non rattachées »)</option>
              {cashLiveRooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}{r.city ? ` — ${r.city}` : ''}</option>
              ))}
            </select>
            {cashLiveRooms.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Les sessions seront regroupées dans une salle par défaut. Vous pouvez en créer depuis la page Cash Live.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Devise</label>
            <select
              value={cashLiveCurrency}
              onChange={(e) => setCashLiveCurrency(e.target.value)}
              disabled={isCashLiveCurrencyLocked}
              className={cn(
                "w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white",
                isCashLiveCurrencyLocked && "opacity-70 cursor-not-allowed"
              )}
            >
              {CURRENCIES.map((c) => (<option key={c.code} value={c.code}>{c.label}</option>))}
            </select>
            {isCashLiveCurrencyLocked ? (
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Devise verrouillée par la poker room ({lockedCashLiveRoomCurrency})
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Limite</label>
              <select value={cashLiveLimits} onChange={(e) => setCashLiveLimits(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white">
                {LIMITS.map((l) => (<option key={l} value={l}>{l}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Variante</label>
              <select value={cashLiveVariant} onChange={(e) => setCashLiveVariant(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white">
                {VARIANTS.map((v) => (<option key={v} value={v}>{v}</option>))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Buy-in ({cashLiveCurrency}) *</label>
              <Input type="text" inputMode="decimal" placeholder="0" value={cashLiveBuyIn} onChange={(e) => setCashLiveBuyIn(e.target.value)} className="rounded-lg border border-gray-200 dark:border-gray-600 w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Cashout ({cashLiveCurrency}) *</label>
              <Input type="text" inputMode="decimal" placeholder="0" value={cashLiveCashout} onChange={(e) => setCashLiveCashout(sanitizePositiveNumber(e.target.value))} className="rounded-lg border border-gray-200 dark:border-gray-600 w-full" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Durée ({tAuthSignUp('optional')})</label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 overflow-hidden w-24">
                <Input type="number" min="0" value={cashLiveDurationHours} onChange={(e) => setCashLiveDurationHours(e.target.value)} placeholder="0" className="border-0 bg-transparent text-center h-9 w-12 px-1" />
                <span className="text-xs text-gray-500 shrink-0">h</span>
              </div>
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 overflow-hidden w-24">
                <Input type="number" min="0" max="59" value={cashLiveDurationMinutes} onChange={(e) => setCashLiveDurationMinutes(e.target.value)} placeholder="0" className="border-0 bg-transparent text-center h-9 w-12 px-1" />
                <span className="text-xs text-gray-500 shrink-0">min</span>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Notes</label>
            <textarea placeholder="Optionnel" value={cashLiveNotes} onChange={(e) => setCashLiveNotes(e.target.value)} rows={2} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white" />
          </div>
        </div>
      )}

      {/* Bankrolls - masqué pour MTT Live sans bankroll et Session poker room */}
      {!isMTTLiveNoBankroll && !isCashGameLive && (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">

        {bankrolls.length === 0 ? (
          <div className="text-center py-8 relative z-10">
            <Wallet className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('bankrolls.noBankrolls')}
            </p>
            <Button
              type="button"
              variant="outlineGold"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openAddBankrollModal', { detail: { fromSession: true } }));
              }}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t('bankrolls.addBankroll')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {bankrolls.map((bankroll) => {
              const isSelected = selectedBankrolls.has(bankroll.id);
              const selected = selectedBankrolls.get(bankroll.id);
              const currentBalance = parseFloat(bankroll.currentBalance);
              const isMTTOnline = format === 'MTT' && universe === 'Online';
              const isSpinGo = format === 'Spin & Go' || format === 'Spin&Go';

              return (
                <div
                  key={bankroll.id}
                  className={cn(
                    'rounded-xl border-2 transition-all duration-200 overflow-hidden',
                    isSelected
                      ? 'border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-950/20 shadow-sm'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600'
                  )}
                >
                  <label className="flex items-center gap-3 p-4 cursor-pointer min-h-[56px]">
                    <input
                      type={isSpinGo ? 'radio' : 'checkbox'}
                      name={isSpinGo ? 'new-session-spin-bankroll' : undefined}
                      checked={isSelected}
                      onChange={() => handleBankrollToggle(bankroll.id)}
                      className={cn(
                        'w-5 h-5 shrink-0 text-amber-500 border-gray-300 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:ring-offset-2',
                        isSpinGo ? 'rounded-full' : 'rounded'
                      )}
                    />
                    <PokerSiteLogo siteName={bankroll.siteName} size={40} className="w-10 h-10 shrink-0 rounded-lg overflow-hidden" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white truncate">{bankroll.siteName}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatCurrency(currentBalance, bankroll.currency)}</p>
                    </div>
                  </label>
                  {isSelected && format !== 'Cash Game' && !isSpinGo && (
                    <div className="px-4 pb-4 pt-0 border-t border-amber-200/50 dark:border-amber-800/50 bg-white/50 dark:bg-gray-800/30">
                      <div className={cn(
                        'pt-4 gap-4 grid',
                        isMTTOnline ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'
                      )}>
                        {/* Nouveau solde - tous les formats sauf Cash Game (calculé à partir des Gains/Pertes par ligne) */}
                        {format !== 'Cash Game' && (
                        <div>
                          <label htmlFor={`new-${bankroll.id}`} className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                            {t('bankrolls.tableNewBalance')}
                          </label>
                          <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 focus-within:border-gray-900 dark:focus-within:ring-gray-100 dark:focus-within:border-gray-100">
                            <Input
                              id={`new-${bankroll.id}`}
                              type="number"
                              step="0.01"
                              value={selected?.newBalance ?? ''}
                              onChange={(e) => handleNewBalanceChange(bankroll.id, e.target.value)}
                              placeholder={formatCurrency(currentBalance, bankroll.currency)}
                              className="border-0 focus-visible:ring-0 rounded-none bg-transparent"
                            />
                            <span className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 shrink-0">
                              {bankroll.currency}
                            </span>
                          </div>
                        </div>
                        )}

                        {/* MTT Online : Tournois + Buy-in total */}
                        {isMTTOnline && (
                          <>
                            <div>
                              <label htmlFor={`mtt-tournaments-${bankroll.id}`} className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                                {t('bankrolls.tableTournaments')}
                              </label>
                              <Input
                                id={`mtt-tournaments-${bankroll.id}`}
                                type="number"
                                min="0"
                                value={selected?.tournamentCount ?? ''}
                                onChange={(e) => handleMTTBankrollFieldChange(bankroll.id, 'tournamentCount', e.target.value)}
                                placeholder="0"
                                className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100"
                              />
                            </div>
                            <div>
                              <label htmlFor={`mtt-buyin-${bankroll.id}`} className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                                {t('bankrolls.tableBuyInTotal')}
                              </label>
                              <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 focus-within:border-gray-900 dark:focus-within:ring-gray-100 dark:focus-within:border-gray-100">
                                <Input
                                  id={`mtt-buyin-${bankroll.id}`}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={selected?.buyInTotal ?? ''}
                                  onChange={(e) => handleMTTBankrollFieldChange(bankroll.id, 'buyInTotal', e.target.value)}
                                  placeholder="0"
                                  className="border-0 focus-visible:ring-0 rounded-none bg-transparent"
                                />
                                <span className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 shrink-0">
                                  {bankroll.currency}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Durée (MTT Online, Cash Game Online & Spin & Go) — juste au-dessus de Notes */}
      {!isMTTLiveNoBankroll && !isCashGameLive && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <span className="text-sm font-medium">{t('sessionInfo.duration')}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">({tAuthSignUp('optional')})</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 dark:focus-within:ring-gray-100 focus-within:border-gray-900 dark:focus-within:border-gray-100 w-24">
              <Input id="details-duration-hours" type="number" min="0" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} placeholder="0" className="border-0 bg-transparent text-center focus-visible:ring-0 h-9 w-12 px-1" aria-label={t('sessionInfo.durationHours')} />
              <span className="text-xs text-gray-500 shrink-0">h</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 dark:focus-within:ring-gray-100 focus-within:border-gray-900 dark:focus-within:border-gray-100 w-24">
              <Input id="details-duration-minutes" type="number" min="0" max="59" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} placeholder="0" className="border-0 bg-transparent text-center focus-visible:ring-0 h-9 w-12 px-1" aria-label={t('sessionInfo.durationMinutes')} />
              <span className="text-xs text-gray-500 shrink-0">min</span>
            </div>
          </div>
        </div>
      )}

      {/* Notes - masqué pour tournoi isolé (MTT Live sans bankroll) */}
      {format !== 'Cash Game' && !isMTTLiveNoBankroll && (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('notes')}
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('notesPlaceholder')}
          rows={4}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 resize-none"
        />
      </div>
      )}
    </div>
  );
};
